// ═══════════════════════════════════════════════════════════════════════════════
//  src/audio/fx.js
//  EL SONIDO DE LA CASA
//
//  Este motor nacio dentro del Dojo (`dojo/dojoFx.js`) y se quedo alli seis
//  meses. Era el unico sitio del producto que sonaba, y era tambien el unico
//  que se sentia vivo — que no es casualidad. Se saca de ahi para que suene
//  toda la aplicacion.
//
//  ── TODO SE SINTETIZA. CERO ARCHIVOS ───────────────────────────────────────
//  Web Audio API: cero peso en el bundle, cero peticiones de red, cero latencia.
//  Un sonido de acierto que tarda 200 ms en descargarse llega tarde — y en
//  feedback, llegar tarde es no llegar. Ademas funciona sin conexion, que para
//  alguien practicando en el metro no es un detalle.
//
//  ── EL AUDIO NACE EN EL PRIMER GESTO ───────────────────────────────────────
//  Los navegadores bloquean el audio automatico. Un AudioContext creado antes
//  del primer toque nace suspendido y silencia la sesion ENTERA sin avisar de
//  nada. Por eso `escucharPrimerGesto()` se llama una vez al arrancar la app y
//  se desengancha solo.
//
//  ── LO QUE ESTE FICHERO NO HACE ────────────────────────────────────────────
//  No suena durante los ejercicios de voz. Un pitido mientras Spacy habla, o
//  mientras el microfono esta grabando al alumno, no es "premium": es un ruido
//  que se cuela en la grabacion y rompe la unica parte del producto donde el
//  silencio importa. Ver `vozEnCurso()`.
// ═══════════════════════════════════════════════════════════════════════════════

let ctx = null;
let silenciado = false;
let vozActiva = false;

const CLAVE_PREFERENCIA = 'nsl_sonido_v1';

// ── EL SONIDO YA NO SE APAGA ────────────────────────────────────────────────
//
// Hubo un boton de silencio en la cabecera y se quito: robaba protagonismo a
// la navegacion, que es lo que la cabecera tiene que dejar ver. El sonido es
// parte de la experiencia, como en Duolingo, y suena siempre.
//
// Esta linea NO se puede borrar sin mas, y por eso esta aqui explicada: quien
// pulso aquel boton alguna vez tiene guardado un 'off' en su navegador. Si el
// modulo siguiera leyendolo, esa persona se quedaria muda para siempre y sin
// ningun boton para recuperar el sonido — un fallo invisible desde aqui, que
// solo sufre quien lo padece. Asi que la preferencia antigua se ignora y
// ademas se borra, para no dejar rastro que confunda mas adelante.
try {
  localStorage.removeItem(CLAVE_PREFERENCIA);
} catch { /* modo privado: no habia nada que borrar */ }

function ac() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/** Debe llamarse desde un handler de gesto real (click/touch). */
export function unlockAudio() { ac(); }

/**
 * Engancha el desbloqueo al primer gesto de la sesion y se desengancha solo.
 * Se llama UNA vez, al arrancar la aplicacion.
 */
export function escucharPrimerGesto() {
  if (typeof window === 'undefined') return;
  const abrir = () => {
    unlockAudio();
    window.removeEventListener('pointerdown', abrir);
    window.removeEventListener('keydown', abrir);
  };
  window.addEventListener('pointerdown', abrir, { once: true, passive: true });
  window.addEventListener('keydown', abrir, { once: true });
}

/**
 * Silencia la app en memoria, sin guardar nada.
 *
 * No lo usa ninguna pantalla: no hay boton de silencio. Se deja porque el
 * interruptor interno (`silenciado`) sigue existiendo y conviene que haya una
 * sola puerta para tocarlo — si algun dia hace falta callar la app durante una
 * grabacion o un examen, se hace desde aqui y no repartiendo banderas sueltas.
 *
 * No persiste a proposito: una preferencia guardada que nadie puede deshacer
 * es exactamente el problema que se acaba de quitar.
 */
export function setMuted(v) { silenciado = Boolean(v); }

export function isMuted() { return silenciado; }

/**
 * Marca que hay voz en curso: Spacy hablando o el microfono abierto.
 *
 * Mientras esto sea `true`, los sonidos de interfaz callan. No es una
 * preferencia del alumno sino una regla del producto: un "tin" de boton
 * grabado dentro de la respuesta del alumno estropea la medicion de
 * pronunciacion, y un pitido encima de la voz del personaje rompe la
 * inmersion de la Arena, que es justo lo que se esta vendiendo.
 */
// Ningun ejercicio de voz dura mas de tres minutos: la Arena son dos, y una
// grabacion del Laboratorio, segundos. Si la señal lleva mas encendida es que
// alguien se la dejo puesta.
const VOZ_MAX_MS = 3 * 60 * 1000;
let vozDesde = 0;

export function vozEnCurso(v) {
    vozActiva = Boolean(v);
    vozDesde = vozActiva ? Date.now() : 0;
}

// ── LA RED, Y POR QUE HACE FALTA ─────────────────────────────────────────────
// Esta señal apaga TODOS los sonidos de interfaz. Si se queda encendida, la app
// entera enmudece — y no da ningun error: parece que el sonido no se implemento.
// Costo encontrarlo precisamente por eso.
//
// Se quedaba pegada por dos caminos: el Laboratorio no la apagaba al
// desmontarse, y la Arena cerraba sus sockets pero se olvidaba de esto. Los dos
// estan arreglados; esto es el cinturon para el tercero que aparezca.
function vozSigueViva() {
    if (!vozActiva) return false;
    if (Date.now() - vozDesde > VOZ_MAX_MS) {
        vozActiva = false;      // se quedo colgada: se suelta sola
        return false;
    }
    return true;
}

// Los sonidos de RESULTADO (acierto, error, cierre) sí suenan aunque haya voz:
// llegan cuando la voz ya termino y son la recompensa del ejercicio.
function calla(esDeInterfaz) {
  return silenciado || (esDeInterfaz && vozSigueViva());
}

/**
 * Un tono con envolvente. Todo lo demás se construye encimando estos.
 * @param {number} freq   Hz
 * @param {number} dur    segundos
 * @param {Object} o      { type, gain, delay, sweepTo }
 */
function tone(freq, dur, { type = 'sine', gain = 0.18, delay = 0, sweepTo = null } = {}) {
  const a = ac();
  if (!a || silenciado) return;

  const t0 = a.currentTime + delay;
  const osc = a.createOscillator();
  const env = a.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(30, sweepTo), t0 + dur);

  // Ataque de 8 ms: suficiente para no chasquear, imperceptible como retardo.
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(env).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Ruido corto — sirve para golpes y para el "disco rayado". */
function noise(dur = 0.16, gain = 0.12, sweep = true) {
  const a = ac();
  if (!a || silenciado) return;

  const frames = Math.floor(a.sampleRate * dur);
  const buf = a.createBuffer(1, frames, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);

  const src = a.createBufferSource();
  src.buffer = buf;

  const filter = a.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1800, a.currentTime);
  if (sweep) filter.frequency.exponentialRampToValueAtTime(220, a.currentTime + dur);

  const env = a.createGain();
  env.gain.setValueAtTime(gain, a.currentTime);
  env.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + dur);

  src.connect(filter).connect(env).connect(a.destination);
  src.start();
}

function buzz(pattern) {
  if (silenciado) return;
  try { navigator.vibrate?.(pattern); } catch { /* iOS no lo soporta */ }
}

/* ── Vocabulario de sonidos ─────────────────────────────────────────────────
   Cada uno tiene una intención distinta. Que un acierto y un récord suenen
   igual es desperdiciar la única señal que el alumno procesa sin mirar. */

/** Acierto normal: tercera mayor ascendente, limpia y corta. */
export function fxHit() {
  tone(660, 0.09, { type: 'triangle', gain: 0.16 });
  tone(990, 0.14, { type: 'triangle', gain: 0.13, delay: 0.055 });
  buzz(18);
}

/** Combo: sube con la racha. La recompensa deja de ser plana. */
export function fxCombo(streak) {
  const step = Math.min(streak, 8);
  const base = 620 + step * 62;
  tone(base, 0.08, { type: 'square', gain: 0.1 });
  tone(base * 1.5, 0.16, { type: 'triangle', gain: 0.15, delay: 0.05 });
  tone(base * 2, 0.2, { type: 'sine', gain: 0.1, delay: 0.1 });
  buzz([14, 26, 22]);
}

/** Error: disco rayado. Feo a propósito — tiene que doler un poco. */
export function fxMiss() {
  tone(210, 0.22, { type: 'sawtooth', gain: 0.13, sweepTo: 90 });
  noise(0.2, 0.09);
  buzz([32, 44, 60]);
}

/** Se acabó el tiempo: dos golpes secos y graves. */
export function fxTimeout() {
  tone(160, 0.14, { type: 'square', gain: 0.11 });
  tone(120, 0.24, { type: 'square', gain: 0.11, delay: 0.14, sweepTo: 70 });
  buzz([50, 60, 50]);
}

/** Empieza la ronda. */
export function fxStart() {
  tone(440, 0.07, { type: 'triangle', gain: 0.11 });
  tone(660, 0.07, { type: 'triangle', gain: 0.11, delay: 0.08 });
  tone(880, 0.14, { type: 'triangle', gain: 0.13, delay: 0.16 });
  buzz(24);
}

/** Cierre de sesión: arpegio de cuatro notas. */
export function fxFinish() {
  [523, 659, 784, 1046].forEach((f, i) =>
    tone(f, 0.3, { type: 'triangle', gain: 0.13, delay: i * 0.09 }));
  buzz([20, 40, 20, 40, 60]);
}

/** Récord personal. Es el único sonido que se permite ser largo. */
export function fxRecord() {
  [784, 988, 1175, 1568].forEach((f, i) =>
    tone(f, 0.42, { type: 'sine', gain: 0.14, delay: i * 0.11 }));
  tone(392, 0.7, { type: 'triangle', gain: 0.09, delay: 0.1 });
  buzz([30, 30, 30, 30, 120]);
}

/** Tic del reloj cuando quedan pocos segundos. */
export function fxTick(urgent = false) {
  tone(urgent ? 1180 : 880, 0.035, { type: 'square', gain: urgent ? 0.09 : 0.05 });
}

/* ── Sonidos de INTERFAZ ────────────────────────────────────────────────────
   Los de arriba son de JUEGO: premian o castigan algo que el alumno hizo. Los
   de abajo solo acompañan a la mano, y por eso son mucho mas bajos y mucho mas
   cortos.

   ⚠️ LA REGLA QUE HACE QUE ESTO NO CANSE
   No suena cada clic. Duolingo tampoco: suenan las acciones que AVANZAN algo
   —entrar a una leccion, confirmar, pasar de pantalla— y callan las que no
   —abrir un menu, cambiar un filtro, hacer scroll—. Un sonido en cada toque
   deja de ser informacion en diez minutos y pasa a ser ruido que el alumno
   silencia; y cuando silencia, se lleva por delante tambien los aciertos.

   Estos tres callan si hay voz en curso. Ver `vozEnCurso`. */

/** El toque de una acción principal. Casi un susurro: 0.05 de ganancia. */
export function fxTap() {
  if (calla(true)) return;
  tone(880, 0.045, { type: 'sine', gain: 0.05 });
}

/** Cambio de pantalla o de paso. Dos notas que suben, muy cortas. */
export function fxAvanzar() {
  if (calla(true)) return;
  tone(520, 0.05, { type: 'sine', gain: 0.05 });
  tone(700, 0.07, { type: 'sine', gain: 0.045, delay: 0.045 });
}

/** Volver atrás o cerrar. Las mismas dos notas, al revés. */
export function fxVolver() {
  if (calla(true)) return;
  tone(700, 0.05, { type: 'sine', gain: 0.045 });
  tone(520, 0.07, { type: 'sine', gain: 0.05, delay: 0.045 });
}

/** Algo se desbloquea: una lección, un nivel, un logro. */
export function fxDesbloqueo() {
  if (calla(false)) return;
  tone(587, 0.14, { type: 'triangle', gain: 0.12 });
  tone(880, 0.18, { type: 'triangle', gain: 0.12, delay: 0.1 });
  tone(1175, 0.3, { type: 'sine', gain: 0.11, delay: 0.2 });
  buzz([18, 30, 18]);
}
